import { useEffect, useRef, useState } from 'react'
import { COLORS } from '../../lib/theme'
import { Icon } from '../Icon'
import { DEFAULT_MEGA_MENU_COLUMNS, type MegaMenuColumn } from '../MegaMenu'
import { MenuCard, MenuColumns, MenuFooter } from './menuCard'
import { MENU_CARD, PANEL_TOP, PANEL_TOP_MOBILE, Z, useCloseOnEscape } from './topbarChrome'

export interface SearchPanelLabels {
  placeholder?: string
  close?: string
  dialog?: string
  downloadTitle?: string
}

/**
 * Search on wehunt.app is not a bare field: it is the menu card with a field
 * stacked above the same link columns, so the nav stays reachable while you
 * type. Submitting is the caller's business — this owns only the field.
 */
export function SearchPanel({
  open,
  onClose,
  onSubmit,
  labels,
  columns = DEFAULT_MEGA_MENU_COLUMNS,
  mobile,
  onNavigateTo,
}: {
  open: boolean
  onClose: () => void
  onSubmit?: (query: string) => void
  labels?: SearchPanelLabels
  columns?: MegaMenuColumn[]
  mobile: boolean
  /** See MenuColumns — routes in-app links instead of reloading. */
  onNavigateTo?: (href: string) => void
}) {
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useCloseOnEscape(open, onClose)

  // Focus on open and clear on close, so reopening never shows a stale term.
  useEffect(() => {
    if (open) inputRef.current?.focus()
    else setQuery('')
  }, [open])

  if (!open) return null

  const top = mobile ? PANEL_TOP_MOBILE : PANEL_TOP

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={labels?.dialog ?? 'Search'}
      style={{
        position: 'fixed',
        top,
        left: 0,
        right: 0,
        zIndex: Z.panel,
        display: 'flex',
        justifyContent: 'center',
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          pointerEvents: 'auto',
          width: '100%',
          // Capped at the pill's own width, with the gutter *inside* the cap —
          // which is how the site insets this card 24px within the pill's edges
          // instead of running it flush. The menu panel has no such padding.
          maxWidth: MENU_CARD.searchMaxWidth,
          padding: mobile ? '0 16px' : '0 24px',
          boxSizing: 'border-box',
          maxHeight: `calc(100dvh - ${top + 24}px)`,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
        }}
      >
        <MenuCard mobile={mobile}>
          <form
            role="search"
            onSubmit={(e) => {
              e.preventDefault()
              const trimmed = query.trim()
              if (trimmed) onSubmit?.(trimmed)
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '12px 16px',
              marginBottom: 24,
              background: COLORS.surfaceContainerLowest,
              border: `1px solid ${COLORS.primary}`,
              borderRadius: 8,
            }}
          >
            <Icon name="search" size={20} color={COLORS.primary} />
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={labels?.placeholder ?? 'Search for...'}
              aria-label={labels?.dialog ?? 'Search'}
              autoComplete="off"
              style={{
                flex: 1,
                minWidth: 0,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: 16,
                color: COLORS.onSurface,
              }}
            />
            <button
              type="button"
              onClick={onClose}
              aria-label={labels?.close ?? 'Close search'}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                padding: 4,
                border: 'none',
                background: 'transparent',
                color: COLORS.onSurface,
                cursor: 'pointer',
                transition: 'opacity 0.2s',
                flexShrink: 0,
              }}
            >
              <Icon name="close" size={20} />
            </button>
          </form>

          <MenuColumns columns={columns} onNavigate={onClose} onNavigateTo={onNavigateTo} />
          <MenuFooter title={labels?.downloadTitle} />
        </MenuCard>
      </div>
    </div>
  )
}
